import { useState, useEffect } from 'react';
import { useAuth, ClientAccount } from '../../../contexts/AuthContext';
import ClientDetail from './ClientDetail';

export default function ClientAccounts() {
  const { addClient, updateClient, deleteClient, getClients } = useAuth();
  const [clients, setClients] = useState<ClientAccount[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientAccount | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientAccount | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    isActive: true,
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = () => {
    setClients(getClients());
  };

  const resetForm = () => {
    setFormData({ name: '', username: '', email: '', password: '', confirmPassword: '', isActive: true });
    setFormError('');
    setShowAddForm(false);
    setEditingClient(null);
  };

  const validateForm = (isEdit = false): boolean => {
    if (!formData.name.trim()) { setFormError('Name is required'); return false; }
    if (!formData.username.trim()) { setFormError('Username is required'); return false; }
    if (!formData.email.trim()) { setFormError('Email is required'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { setFormError('Invalid email format'); return false; }
    if (!isEdit || formData.password) {
      if (formData.password.length < 6) { setFormError('Password must be at least 6 characters'); return false; }
      if (formData.password !== formData.confirmPassword) { setFormError('Passwords do not match'); return false; }
    }
    return true;
  };

  const handleAddClient = () => {
    if (!validateForm()) return;
    const result = addClient({
      name: formData.name.trim(),
      username: formData.username.trim(),
      email: formData.email.trim(),
      password: formData.password,
      isActive: formData.isActive,
    });
    if (result.success) { loadClients(); resetForm(); }
    else setFormError(result.error || 'Failed to add client');
  };

  const handleEditClient = () => {
    if (!editingClient || !validateForm(true)) return;
    const updates: Partial<Omit<ClientAccount, 'id' | 'createdAt'>> = {
      name: formData.name.trim(),
      username: formData.username.trim(),
      email: formData.email.trim(),
      isActive: formData.isActive,
    };
    if (formData.password) updates.password = formData.password;
    const result = updateClient(editingClient.id, updates);
    if (result.success) { loadClients(); resetForm(); }
    else setFormError(result.error || 'Failed to update client');
  };

  const handleDeleteClient = (id: string) => {
    if (deleteClient(id)) { loadClients(); setDeleteConfirm(null); }
  };

  const startEdit = (client: ClientAccount) => {
    setEditingClient(client);
    setFormData({ name: client.name, username: client.username, email: client.email || '', password: '', confirmPassword: '', isActive: client.isActive });
    setFormError('');
    setShowAddForm(false);
  };

  const toggleStatus = (client: ClientAccount) => {
    updateClient(client.id, { isActive: !client.isActive });
    loadClients();
  };

  // If a client is selected, show their detail view
  if (selectedClient) {
    return (
      <ClientDetail
        client={selectedClient}
        onBack={() => setSelectedClient(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Client Accounts</h2>
          <p className="text-sm text-gray-500 mt-1">
            {clients.length} {clients.length === 1 ? 'client' : 'clients'} registered — click a client to manage their projects &amp; documents
          </p>
        </div>
        {!showAddForm && !editingClient && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-user-add-line text-base"></i>
            Add Client
          </button>
        )}
      </div>

      {/* Add / Edit Form */}
      {(showAddForm || editingClient) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingClient ? 'Edit Client Account' : 'Add New Client'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                placeholder="Enter full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Username <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                placeholder="Enter username"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address <span className="text-red-500">*</span></label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                placeholder="client@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password {editingClient ? '(leave blank to keep current)' : <span className="text-red-500">*</span>}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                placeholder={editingClient ? 'Leave blank to keep current' : 'Minimum 6 characters'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password {!editingClient && <span className="text-red-500">*</span>}
              </label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                placeholder="Confirm password"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 border-gray-300 rounded cursor-pointer"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">Active (client can log in)</label>
          </div>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{formError}</p>
            </div>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={editingClient ? handleEditClient : handleAddClient}
              className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              {editingClient ? 'Update Client' : 'Create Client'}
            </button>
            <button
              onClick={resetForm}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Clients Grid */}
      {clients.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-user-line text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No clients yet</h3>
          <p className="text-sm text-gray-600 mb-4">Create your first client account to get started</p>
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-user-add-line text-base"></i>
              Add First Client
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((client) => {
            // Count documents for this client
            const stored = localStorage.getItem('admin_data_items');
            const allItems = stored ? JSON.parse(stored) : [];
            const docCount = allItems.filter((i: { clientId: string }) => i.clientId === client.id).length;
            const projectSet = new Set(allItems.filter((i: { clientId: string; projectNumber?: string }) => i.clientId === client.id && i.projectNumber).map((i: { projectNumber: string }) => i.projectNumber));
            const projectCount = projectSet.size;

            return (
              <div
                key={client.id}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-400 hover:shadow-md transition-all group"
              >
                {/* Top row */}
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="flex items-center gap-3 cursor-pointer flex-1 min-w-0"
                    onClick={() => setSelectedClient(client)}
                  >
                    <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">
                        {client.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{client.name}</p>
                      <p className="text-xs text-gray-500 truncate">@{client.username}</p>
                      {client.email && (
                        <p className="text-xs text-gray-400 truncate">{client.email}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleStatus(client)}
                    className="flex-shrink-0 cursor-pointer ml-2"
                  >
                    {client.isActive ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        <i className="ri-checkbox-circle-fill"></i> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                        <i className="ri-close-circle-fill"></i> Inactive
                      </span>
                    )}
                  </button>
                </div>

                {/* Stats */}
                <div
                  className="flex items-center gap-4 mb-4 cursor-pointer"
                  onClick={() => setSelectedClient(client)}
                >
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <i className="ri-folder-line text-gray-400"></i>
                    <span>{projectCount} {projectCount === 1 ? 'project' : 'projects'}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <i className="ri-file-line text-gray-400"></i>
                    <span>{docCount} {docCount === 1 ? 'document' : 'documents'}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => setSelectedClient(client)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-folder-open-line"></i>
                    Manage
                  </button>
                  <button
                    onClick={() => startEdit(client)}
                    className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                    title="Edit client"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  {deleteConfirm === client.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDeleteClient(client.id)}
                        className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(client.id)}
                      className="w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      title="Delete client"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}